/**
 * @countriesdb/widget
 * 
 * Plain JavaScript widget for CountriesDB.
 * Provides DOM manipulation and auto-initialization for country/subdivision selects.
 */

import type { WidgetOptions } from './types';
import type { WidgetConfig } from '@countriesdb/widget-core';
import { setupCountrySelection, setupSubdivisionSelection, updateSubdivisions } from './initialization';
import type { SelectElement, WidgetState, EventHandlers } from './types';

// Global namespace to prevent double initialization
const NS_KEY = '__CountriesWidgetNS__';

interface WidgetNamespace {
  initialized: boolean;
  initPromise: Promise<boolean> | null;
  version: number;
  eventHandlers: WeakMap<SelectElement, EventHandlers>;
}

declare global {
  interface Window {
    [NS_KEY]: WidgetNamespace;
    CountriesWidgetLoad: typeof CountriesWidgetLoad;
  }
}

/**
 * Main widget initialization function
 */
async function CountriesWidgetLoad(
  options: WidgetOptions = {} as WidgetOptions
): Promise<boolean> {
  // Initialize namespace
  if (!window[NS_KEY]) {
    window[NS_KEY] = {
      initialized: false,
      initPromise: null,
      version: 0,
      eventHandlers: new WeakMap<SelectElement, EventHandlers>(),
    };
  }

  const NS = window[NS_KEY];
  
  // Ensure eventHandlers exists (for backwards compatibility)
  if (!NS.eventHandlers) {
    NS.eventHandlers = new WeakMap<SelectElement, EventHandlers>();
  }

  // Handle reload option - reset state to force re-initialization
  const shouldReload = (options as any).reload === true;
  if (shouldReload) {
    NS.initialized = false;
    NS.initPromise = null;
    
    // Clear consumed preselected flags to allow preselected values to be reapplied on reload: true
    // This ensures full reload behavior where preselected values are reapplied
    if (typeof document !== 'undefined') {
      const subdivisionSelects = document.querySelectorAll('.subdivision-selection');
      subdivisionSelects.forEach((select) => {
        if ((select as HTMLElement).dataset._widgetPreselectedConsumed) {
          delete (select as HTMLElement).dataset._widgetPreselectedConsumed;
        }
      });
    }
  }

  // Share the same promise across concurrent calls
  NS.initPromise = (async () => {
    // Wait for DOM if needed
    if (document.readyState === 'loading') {
      await new Promise<void>((resolve) => {
        document.addEventListener('DOMContentLoaded', () => resolve(), {
          once: true,
        });
      });
    }

    // Get configuration from options or script URL
    const config = getConfigFromOptionsOrScript(options);

    // Fix mis-classed elements early (DOM cleanup that should always happen)
    fixMisClassedElements();

    // Check for conflicting parameters
    if (config.followRelated && config.followUpward) {
      showParamConflictError();
      return false;
    }

    // Initialize widget state
    // Use global eventHandlers from namespace so we can clean up old handlers on re-initialization
    const state: WidgetState = {
      countriesMap: new WeakMap(),
      subdivisionsMap: new WeakMap(),
      subdivisionsLanguageMap: new WeakMap(),
      isInitializing: new Set(),
      eventHandlers: NS.eventHandlers, // Use global WeakMap that persists across calls
    };

    // Use empty string if publicKey is missing (will show error when API calls fail)
    const apiKey = config.publicKey || '';

    // Setup subdivisions first (they depend on countries)
    await setupSubdivisionSelection(apiKey, config.backendUrl, state, {
      followRelated: config.followRelated || false,
      followUpward: config.followUpward || false,
      showSubdivisionType: config.showSubdivisionType !== false,
      allowParentSelection: config.allowParentSelection || false,
      preferOfficialSubdivisions: config.preferOfficialSubdivisions || false,
      subdivisionRomanizationPreference: config.subdivisionRomanizationPreference,
      preferLocalVariant: config.preferLocalVariant || false,
      subdivisionNameFilter: config.subdivisionNameFilter,
    });

    const subdivisionConfig = {
      followRelated: config.followRelated || false,
      followUpward: config.followUpward || false,
      showSubdivisionType: config.showSubdivisionType !== false,
      allowParentSelection: config.allowParentSelection || false,
      preferOfficialSubdivisions: config.preferOfficialSubdivisions || false,
      subdivisionRomanizationPreference: config.subdivisionRomanizationPreference,
      preferLocalVariant: config.preferLocalVariant || false,
      forcedLanguage: config.forcedLanguage,
      defaultLanguage: config.defaultLanguage,
      subdivisionNameFilter: config.subdivisionNameFilter,
    };

    // Setup countries
    await setupCountrySelection(
      apiKey,
      config.backendUrl,
      state,
      {
        defaultLanguage: config.defaultLanguage,
        forcedLanguage: config.forcedLanguage,
        isoCountryNames: config.isoCountryNames || false,
        followRelated: config.followRelated || false,
        followUpward: config.followUpward || false,
        countryNameFilter: config.countryNameFilter,
      },
      subdivisionConfig
    );

    // After countries are set up, update subdivisions for any preselected countries
    const countrySelects = Array.from(
      document.querySelectorAll<SelectElement>('.country-selection')
    );
    for (const select of countrySelects) {
      if (
        select.value &&
        select.value !== (select.dataset.defaultValue || '')
      ) {
        await updateSubdivisions(select, apiKey, config.backendUrl, state, subdivisionConfig);
      }
    }

    NS.initialized = true;
    return true;
  })();

  return NS.initPromise;
}

/**
 * Get configuration from options or script URL parameters
 */
function getConfigFromOptionsOrScript(
  options: WidgetOptions
): WidgetConfig & { backendUrl: string } {
  // Check for global config first (for bundled widgets that need config before auto-init)
  const globalConfig = typeof window !== 'undefined' && (window as any).CountriesDBConfig
    ? (window as any).CountriesDBConfig
    : null;
  
  // Try to get config from script URL (for backward compatibility with widget.blade.php)
  let scriptUrl: URL | null = null;
  try {
    let loaderScript: HTMLScriptElement | null = null;
    
    // First try document.currentScript (works during script execution)
    // But only if it matches the widget pattern (not a bundled file)
    if (document.currentScript && document.currentScript instanceof HTMLScriptElement) {
      const src = document.currentScript.src;
      if (src && (src.includes('@countriesdb/widget') || src.includes('widget/dist/index.js'))) {
        loaderScript = document.currentScript;
      }
    }
    
    // If currentScript didn't match, search for widget script
    if (!loaderScript) {
      const scripts = Array.from(document.getElementsByTagName('script'));
      loaderScript = scripts.find((s) => 
        s.src && (
          s.src.includes('@countriesdb/widget') ||
          s.src.includes('widget/dist/index.js')
        )
      ) as HTMLScriptElement || null;
    }
    
    if (loaderScript && loaderScript.src) {
      scriptUrl = new URL(loaderScript.src);
    }
  } catch {
    // Ignore errors
  }

  const config: WidgetConfig & { backendUrl: string } = {
    // Priority: options > globalConfig > scriptUrl params > defaults
    publicKey: options.publicKey ?? globalConfig?.publicKey ?? scriptUrl?.searchParams.get('public_key') ?? '',
    backendUrl: options.backendUrl ?? globalConfig?.backendUrl ?? scriptUrl?.searchParams.get('backend_url') ?? getDefaultBackendUrl(),
    defaultLanguage: options.defaultLanguage ?? globalConfig?.defaultLanguage ?? scriptUrl?.searchParams.get('default_language') ?? undefined,
    forcedLanguage: options.forcedLanguage ?? globalConfig?.forcedLanguage ?? scriptUrl?.searchParams.get('forced_language') ?? undefined,
    showSubdivisionType: options.showSubdivisionType !== undefined
      ? options.showSubdivisionType
      : globalConfig?.showSubdivisionType !== undefined
        ? globalConfig.showSubdivisionType
        : parseBoolean(scriptUrl?.searchParams.get('show_subdivision_type') ?? '1'),
    followRelated: options.followRelated !== undefined
      ? options.followRelated
      : globalConfig?.followRelated !== undefined
        ? globalConfig.followRelated
        : parseBoolean(scriptUrl?.searchParams.get('follow_related') ?? 'false'),
    followUpward: options.followUpward !== undefined
      ? options.followUpward
      : globalConfig?.followUpward !== undefined
        ? globalConfig.followUpward
        : parseBoolean(scriptUrl?.searchParams.get('follow_upward') ?? 'false'),
    allowParentSelection: options.allowParentSelection !== undefined
      ? options.allowParentSelection
      : globalConfig?.allowParentSelection !== undefined
        ? globalConfig.allowParentSelection
        : parseBoolean(scriptUrl?.searchParams.get('allow_parent_selection') ?? 'false'),
    isoCountryNames: options.isoCountryNames !== undefined
      ? options.isoCountryNames
      : globalConfig?.isoCountryNames !== undefined
        ? globalConfig.isoCountryNames
        : parseBoolean(scriptUrl?.searchParams.get('iso_country_names') ?? 'false'),
    subdivisionRomanizationPreference:
      options.subdivisionRomanizationPreference ||
      globalConfig?.subdivisionRomanizationPreference ||
      scriptUrl?.searchParams.get('subdivision_romanization_preference') ||
      undefined,
    preferLocalVariant: options.preferLocalVariant !== undefined
      ? options.preferLocalVariant
      : globalConfig?.preferLocalVariant !== undefined
        ? globalConfig.preferLocalVariant
        : parseBoolean(scriptUrl?.searchParams.get('prefer_local_variant') ?? 'false'),
    preferOfficialSubdivisions: options.preferOfficialSubdivisions !== undefined
      ? options.preferOfficialSubdivisions
      : globalConfig?.preferOfficialSubdivisions !== undefined
        ? globalConfig.preferOfficialSubdivisions
        : parseBoolean(scriptUrl?.searchParams.get('prefer_official') ?? 'false'),
    countryNameFilter: options.countryNameFilter ?? globalConfig?.countryNameFilter,
    subdivisionNameFilter: options.subdivisionNameFilter ?? globalConfig?.subdivisionNameFilter,
    autoInit: options.autoInit !== undefined
      ? options.autoInit
      : globalConfig?.autoInit !== undefined
        ? globalConfig.autoInit
        : parseBoolean(scriptUrl?.searchParams.get('auto_init') ?? 'true'),
  };

  // Resolve filter functions from global scope if specified by name
  if (scriptUrl) {
    const countryNameFilterName = scriptUrl.searchParams.get('countryNameFilter');
    if (countryNameFilterName && typeof window !== 'undefined') {
      const filter = (window as any)[countryNameFilterName];
      if (typeof filter === 'function') {
        config.countryNameFilter = filter;
      }
    }

    const subdivisionNameFilterName = scriptUrl.searchParams.get('subdivisionNameFilter');
    if (subdivisionNameFilterName && typeof window !== 'undefined') {
      const filter = (window as any)[subdivisionNameFilterName];
      if (typeof filter === 'function') {
        config.subdivisionNameFilter = filter;
      }
    }
  }

  return config;
}

/**
 * Get default backend URL
 */
function getDefaultBackendUrl(): string {
  // Always default to API domain
  // Users should explicitly set backendUrl if they need a custom one
  return 'https://api.countriesdb.com';
}

/**
 * Fix mis-classed elements (e.g., country-selection with subdivision-selection class)
 * This should happen early, before API calls, as it's just DOM cleanup
 */
function fixMisClassedElements(): void {
  const countrySelects = Array.from(
    document.querySelectorAll<HTMLSelectElement>('.country-selection')
  );
  
  for (const select of countrySelects) {
    // Avoid conflict if mis-classed
    if (select.classList.contains('subdivision-selection')) {
      select.classList.remove('subdivision-selection');
      select.classList.add('subdivision-selection-removed');
    }
  }
}

/**
 * Parse boolean from string value
 */
function parseBoolean(value: string | null | undefined): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  const lowered = String(value).trim().toLowerCase();
  return !(lowered === '0' || lowered === 'false');
}

/**
 * Show error when both follow_related and follow_upward are enabled
 */
function showParamConflictError(): void {
  const errorMessage = 'Error: Cannot enable both follow_related and follow_upward';
  const countrySelects = Array.from(
    document.querySelectorAll<SelectElement>('.country-selection')
  );
  for (const select of countrySelects) {
    select.innerHTML = `<option value="${select.dataset.defaultValue ?? ''}" disabled>${errorMessage}</option>`;
  }
  const subdivisionSelects = Array.from(
    document.querySelectorAll<SelectElement>('.subdivision-selection')
  );
  for (const select of subdivisionSelects) {
    select.innerHTML = `<option value="${select.dataset.defaultValue ?? ''}" disabled>${errorMessage}</option>`;
  }
}

// Expose public loader
if (typeof window !== 'undefined') {
  window.CountriesWidgetLoad = CountriesWidgetLoad;

  // Auto-init if script URL has auto_init=true (or not set, default is true)
  // Use a function that waits for DOM to be ready and finds the script tag reliably
  (function checkAutoInit() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', checkAutoInit, { once: true });
      return;
    }

    // Find the script tag that loaded this widget
    let loaderScript: HTMLScriptElement | null = null;
    
    // First try document.currentScript (works during script execution)
    // But only if it matches the widget pattern (not a bundled file)
    if (document.currentScript && document.currentScript instanceof HTMLScriptElement) {
      const src = document.currentScript.src;
      if (src && (src.includes('@countriesdb/widget') || src.includes('widget/dist/index.js'))) {
        loaderScript = document.currentScript;
      }
    }
    
    // If currentScript didn't match, search for widget script
    if (!loaderScript) {
      const scripts = Array.from(document.getElementsByTagName('script'));
      loaderScript = scripts.find((s) => 
        s.src && (
          s.src.includes('@countriesdb/widget') ||
          s.src.includes('widget/dist/index.js')
        )
      ) as HTMLScriptElement || null;
    }
    
    // Default to auto-init = true (only disable if explicitly set to false)
    let shouldAutoInit = true;
    
    const globalConfig = typeof window !== 'undefined'
      ? (window as any).CountriesDBConfig || null
      : null;
    
    if (globalConfig && typeof globalConfig.autoInit !== 'undefined') {
      shouldAutoInit = !!globalConfig.autoInit;
    } else if (loaderScript && loaderScript.src) {
      try {
        const scriptUrl = new URL(loaderScript.src);
        const autoInit = scriptUrl.searchParams.get('auto_init');
        // Only disable if explicitly set to false/0
        shouldAutoInit = autoInit === null || autoInit === 'true' || autoInit === '1';
      } catch {
        // If URL parsing fails, default to true (auto-init enabled)
        shouldAutoInit = true;
      }
    }
    
    // Auto-init by default (unless explicitly disabled)
    if (shouldAutoInit) {
      // Use setTimeout to ensure script tag is fully processed
      setTimeout(() => {
        CountriesWidgetLoad().catch(console.error);
      }, 0);
    }
  })();
}

// Export for ES modules
export default CountriesWidgetLoad;

